import h from 'hyperscript';

import { closePopup } from 'ui/popup/popup';
import { RadioGroup } from 'ui/radio-group/radio-group';
import { HelpTopicLink } from 'ui/help-topic-link/help-topic-link';

import { uniqueId } from 'cadenza/utils/unique-id';
import 'cadenza/conditions/like-condition/like-condition-dropdown.css';
import i18n from 'cadenza/conditions/like-condition/messages.properties';
import type { CaseSensitivity } from 'cadenza/conditions/model/case-sensitivity';
import { CombinationType } from 'cadenza/conditions/model/combination-type';
import { FilterType } from 'cadenza/conditions/model/filter-type';
import type { DataSourceType } from 'cadenza/repository/model/data-source-type';
import { hasCapability } from 'cadenza/data-source-util';
import type { WorkbookDataSourceDto } from 'cadenza/workbook/model/workbook-data-source-dto';

const COMPONENT_NAME = 'd-like-condition-dropdown';

export const DEFAULT_FILTERTYPE: FilterType = 'SUBSTRING';
export const DEFAULT_CASESENSITIVITY: CaseSensitivity = 'INSENSITIVE';
export const DEFAULT_COMBINATIONTYPE: CombinationType = 'OR';

export class LikeConditionDropdown extends HTMLElement {

  _filterTypeRadioGroup!: RadioGroup;
  _caseSensitivityCheckBox?: HTMLInputElement;
  _combinationTypeRadioGroup!: RadioGroup;
  _simplifiedConfiguration: boolean;
  _dataSource?: WorkbookDataSourceDto;

  constructor ({
    filterType,
    caseSensitivity,
    combinationType,
    simplifiedConfiguration,
    dataSource
  }: {
    filterType: FilterType;
    caseSensitivity: CaseSensitivity;
    combinationType: string;
    simplifiedConfiguration: boolean;
    dataSource: WorkbookDataSourceDto | undefined;
  }
  = {
    filterType: DEFAULT_FILTERTYPE,
    caseSensitivity: DEFAULT_CASESENSITIVITY,
    combinationType: DEFAULT_COMBINATIONTYPE,
    simplifiedConfiguration: false,
    dataSource: undefined
  }) {
    super();
    this._dataSource = dataSource;
    this._simplifiedConfiguration = simplifiedConfiguration;
    this._createDialogContent(filterType, caseSensitivity, combinationType, simplifiedConfiguration, dataSource?.type ?? 'UNDEFINED');
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    if (this._simplifiedConfiguration) {
      this.classList.add('simplified-config');
    }
  }

  get returnValue () {
    const filterType = this._filterTypeRadioGroup.value as FilterType;
    if (this._simplifiedConfiguration && filterType === 'PATTERN') {
      return { filterType };
    }
    const caseSensitivity = this._isCaseSensitiveSelected() ? 'SENSITIVE' : 'INSENSITIVE';
    const combinationType = this._combinationTypeRadioGroup.value;
    return { filterType, caseSensitivity, combinationType };
  }

  _createDialogContent (filterType: FilterType, caseSensitivity: CaseSensitivity, combinationType: string, simplifiedConfiguration: boolean, dataSourceType: DataSourceType) {
    const topicId = 'filters--ela-like-query-syntax';
    const filterTypeOptions = FilterType.values.map(type => ({
      value: type,
      label: i18n(`filtertype.${type.toLowerCase()}`),
      description: type === 'PATTERN' && dataSourceType === 'ELASTIC'
        ? new HelpTopicLink({
          topicId,
          linkText: i18n(`linkText.<${topicId}>`),
          variant: 'textlink',
          title: i18n(`linkText.<${topicId}>`)
        })
        : undefined
    }));
    this._filterTypeRadioGroup = new RadioGroup(filterTypeOptions, {
      initialValue: filterType || DEFAULT_FILTERTYPE
    });

    if (simplifiedConfiguration) {
      this._filterTypeRadioGroup.onchange = () => {
        this._adjustConfigVisibility();
      };
      this._adjustConfigVisibility(filterType);
    }

    const combinationTypeLabelId = uniqueId();
    const combinationTypeOptions = CombinationType.values.map(type => ({
      value: type, label: i18n(`combinationtype.${type.toLowerCase()}`)
    }));
    this._combinationTypeRadioGroup = new RadioGroup(combinationTypeOptions, {
      initialValue: combinationType || DEFAULT_COMBINATIONTYPE
    });
    this._combinationTypeRadioGroup.setAttribute('aria-labelledby', combinationTypeLabelId);

    this.append(h(`.form-group.${COMPONENT_NAME}--filter-type`, [ this._filterTypeRadioGroup ]));
    if (this._dataSourceSupportsCaseInsensitive()) {
      this._caseSensitivityCheckBox = h('input', {
        type: 'checkbox',
        name: 'caseSensitivity',
        checked: caseSensitivity === 'SENSITIVE'
      });
      this.append(h(`label.d-form-check.${COMPONENT_NAME}--case-sensitivity`, [
        this._caseSensitivityCheckBox,
        i18n('casesensitivity.label') ]));
    }
    this.append(
      h(`h4.${COMPONENT_NAME}--combination-type-header`, { id: combinationTypeLabelId }, i18n('combinationtype.label')),
      h(`.form-group.${COMPONENT_NAME}--combination-type`, [ this._combinationTypeRadioGroup ]));

    this.addEventListener('keydown', event => {
      if (event.keyCode === 13) {
        closePopup();
      }
    });
  }

  _isCaseSensitiveSelected () {
    return !this._dataSourceSupportsCaseInsensitive() || this._caseSensitivityCheckBox?.checked;
  }

  _dataSourceSupportsCaseInsensitive () {
    if (!this._dataSource) {
      return true;
    }
    return hasCapability(this._dataSource, 'CASE_INSENSITIVE_LIKE_CONDITION');
  }

  _adjustConfigVisibility (filterType?: FilterType) {
    this.classList.toggle('simplified-pattern-config', (filterType || this._filterTypeRadioGroup.value) === 'PATTERN');
  }

}

customElements.define(COMPONENT_NAME, LikeConditionDropdown);
